import { useEffect, useState } from "react";
import { LineChart as LineChartIcon } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { TIMELINE } from "@/constants/testIds/app";

export default function Timeline() {
  const [data, setData] = useState({ moods: [], journals: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/timeline");
        setData(data);
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const chartData = data.moods.map((m) => {
    const emotions = (m.emotions && m.emotions.length > 0) ? m.emotions : (m.emotion ? [m.emotion] : []);
    return {
      date: new Date(m.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
      intensity: m.intensity,
      emotion: emotions.join(", "),
    };
  });

  const themeCounts = {};
  data.journals.forEach((j) => (j.themes || []).forEach((t) => (themeCounts[t] = (themeCounts[t] || 0) + 1)));
  const topThemes = Object.entries(themeCounts).sort((a, b) => b[1] - a[1]).slice(0, 8);

  return (
    <div data-testid={TIMELINE.container} className="max-w-5xl">
      <div className="label-caps mb-4">Timeline</div>
      <h1 className="font-serif-display text-4xl sm:text-5xl text-[#2C2520] tracking-tight">
        Your feelings, drawn softly.
      </h1>

      {loading ? (
        <p className="mt-10 text-[#2C2520]/60 italic font-serif-display">Tracing the line…</p>
      ) : chartData.length === 0 && data.journals.length === 0 ? (
        <div data-testid={TIMELINE.empty} className="mt-14 border border-dashed border-[#E8E3D5] rounded-[12px] p-12 text-center bg-[#F5F2EA]/40">
          <LineChartIcon className="w-8 h-8 text-[#4A5D4E]/50 mx-auto" strokeWidth={1.5} />
          <p className="mt-4 font-serif-display italic text-lg text-[#2C2520]/70">
            No entries yet.
          </p>
        </div>
      ) : (
        <div className="space-y-10 mt-10">
          <div className="bg-[#F5F2EA] border border-[#E8E3D5] rounded-[12px] p-6 sm:p-8">
            <div className="label-caps mb-4">Mood intensity</div>
            <div data-testid={TIMELINE.chart} className="w-full h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 12, right: 12, bottom: 8, left: -20 }}>
                  <CartesianGrid stroke="#E8E3D5" vertical={false} />
                  <XAxis dataKey="date" tick={{ fill: "#2C2520", fontSize: 12 }} stroke="#E8E3D5" />
                  <YAxis domain={[0, 5]} tick={{ fill: "#2C2520", fontSize: 12 }} stroke="#E8E3D5" />
                  <Tooltip
                    contentStyle={{ background: "#FDFBF7", border: "1px solid #E8E3D5", borderRadius: 12 }}
                    labelStyle={{ color: "#2C2520" }}
                  />
                  <Line
                    type="monotone"
                    dataKey="intensity"
                    stroke="#4A5D4E"
                    strokeWidth={2.5}
                    dot={{ fill: "#4A5D4E", r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {topThemes.length > 0 && (
            <div className="bg-[#F5F2EA] border border-[#E8E3D5] rounded-[12px] p-6 sm:p-8">
              <div className="label-caps mb-4">Recurring themes</div>
              <div className="flex flex-wrap gap-2">
                {topThemes.map(([t, count]) => (
                  <span
                    key={t}
                    className="px-3 py-1.5 rounded-full bg-[#FDFBF7] border border-[#E8E3D5] text-sm text-[#2C2520]"
                  >
                    {t} <span className="text-[#4A5D4E]">·{count}</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="bg-[#F5F2EA] border border-[#E8E3D5] rounded-[12px] p-6 sm:p-8">
            <div className="label-caps mb-4">Recent moods</div>
            <ul className="divide-y divide-[#E8E3D5]">
              {[...data.moods].reverse().slice(0, 10).map((m) => {
                const emotions = (m.emotions && m.emotions.length > 0) ? m.emotions : (m.emotion ? [m.emotion] : []);
                return (
                  <li key={m.id} className="py-3 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <span className="font-serif-display text-lg text-[#2C2520] capitalize">
                        {emotions.join(", ") || "unspecified"}
                      </span>
                      <span className="text-sm text-[#2C2520]/60 ml-3">{m.intensity}/5</span>
                    </div>
                    <span className="text-xs text-[#2C2520]/55 flex-shrink-0">
                      {new Date(m.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
